import { createSlice, createAsyncThunk, createSelector } from '@reduxjs/toolkit'
import API from 'utils/api'
import { buildUrl, clamp } from 'utils/helpers'
import config from 'config'
import { CLEAR_RESOURCES } from 'store/resources/types'
import { CLEAR_STATUS } from 'store/ui/types'
import { showErrorMessage } from 'store/ui/actions'
import i18next from 'i18next'

export const fetchAvailable = createAsyncThunk(
  'installations/fetchAvailable',
  async (params, { getState, rejectWithValue, dispatch }) => {
    const { q, order = 'desc', page = 1, limit = 25 } = params
    params.with_gates = true
    try {
      const payload = await API.request(
        buildUrl('/installation/devices', params),
        {
          dispatch,
        },
        `${config.redirectUri}/api/v2`
      )
      return payload
    } catch {
      return rejectWithValue()
    }
  }
)

export const linkDevice = createAsyncThunk(
  'installations/linkDevice',
  async (params, { getState, rejectWithValue, dispatch }) => {
    try {
      const payload = await API.request(
        buildUrl(`/installation_points/link_device`, params),
        {
          method: 'PUT',
        }
      )
      return payload
    } catch (err) {
      const { message } = await err.json()
      return rejectWithValue(message)
    }
  }
)

// Тело ошибки не всегда JSON: 500 от Rails и 502/504 от ingress приходят HTML, а обрыв
// сети реджектит fetch объектом TypeError без .json(). Без этой обёртки разбор падает
// внутри catch, thunk отклоняется объектом ошибки, и он доезжает до рендера как текст.
const errorMessage = async (err) => {
  if (typeof err?.json !== 'function') return null

  const body = await err.json().catch(() => null)
  return body?.error || body?.message || null
}

// Ключи, залитые в контроллер Eltex напрямую. Адаптер приводит базу RFID контроллера
// к списку ключей платформы, поэтому перед установкой на подъезд платформа должна стать
// надмножеством устройства: читаем ключи и добавляем их на подъезд (business_apps#1468).
export const fetchDeviceKeys = createAsyncThunk(
  'installations/fetchDeviceKeys',
  async ({ uuid }, { rejectWithValue }) => {
    try {
      const payload = await API.request(
        `/foreign_intercoms/${uuid}/device_keys`
      )
      return payload
    } catch (err) {
      return rejectWithValue(await errorMessage(err))
    }
  }
)

// file — CSV-экспорт из веб-интерфейса контроллера. Нужен, когда прошивка отдаёт
// неполный список по API: без него бэкенд перечитывает устройство сам.
export const importDeviceKeys = createAsyncThunk(
  'installations/importDeviceKeys',
  async ({ uuid, entryUuid, file }, { dispatch, rejectWithValue }) => {
    try {
      const body = new FormData()
      body.append('entry_uuid', entryUuid)
      if (file) body.append('file', file)

      const payload = await API.request(
        `/foreign_intercoms/${uuid}/import_device_keys`,
        {
          method: 'POST',
          body,
          // Content-Type проставляет сам FormData (нужна граница multipart), язык
          // ошибок бэкенда задаём явно — иначе тексты придут не на языке интерфейса.
          headers: new Headers({ 'Accept-Language': i18next.language }),
        }
      )
      return payload
    } catch (err) {
      const message =
        (await errorMessage(err)) ||
        i18next.t('devices:eltex_keys.import_error')
      dispatch(showErrorMessage(message))
      return rejectWithValue(message)
    }
  }
)

export const manualGateDevice = createAsyncThunk(
  '',
  async (params, { getState, rejectWithValue, dispatch }) => {
    try {
      const payload = await API.request(
        buildUrl(`/installation_points`, params),
        {
          method: 'GET',
        }
      )
      return payload
    } catch (err) {
      const { message } = await err.json()
      return rejectWithValue(message)
    }
  }
)

// Resolves an address (text and/or lat/lon) to a house/entry/gate and
// creates the installation point on the backend. Currently backed by a
// stub on the GIS side (returns the most recent house/entry/gate) until
// the real geolocate/create pipeline is wired up there.
export const createInstallationPoint = createAsyncThunk(
  'installations/createInstallationPoint',
  async (params, { dispatch, rejectWithValue }) => {
    try {
      const payload = await API.request(
        buildUrl('/installation_points', params),
        {
          method: 'POST',
        }
      )
      return payload
    } catch (err) {
      const { message } = await err.json()
      dispatch(showErrorMessage(message))
      return rejectWithValue(message)
    }
  }
)

const initialState = {
  available: [],
  loadingAvailable: false,
  search: '',
  msg: '',
  error: '',
  linkPending: false,
  geolocatePending: false,
}

// reducers
export const installationsSlice = createSlice({
  name: 'installations',
  initialState,
  reducers: {
    incPage: (state) => {
      state.page = clamp(state.page + 1, 1, state.pages)
    },
    decPage: (state) => {
      state.page = clamp(state.page - 1, 1, state.pages)
    },
    setSearch: (state, { payload }) => {
      state.search = payload
    },
  },
  extraReducers: {
    [fetchAvailable.pending]: (state) => {
      state.loadingAvailable = true
    },
    [fetchAvailable.rejected]: (state) => {
      state.loadingAvailable = false
    },
    [fetchAvailable.fulfilled]: (state, { payload }) => {
      state.loadingAvailable = false
      state.available = payload.devices.map((device) => ({
        label: device.serial_number,
        value: device.uuid,
        type: device.type,
        model: device.model,
      }))
    },
    [linkDevice.pending]: (state) => {
      state.linkPending = true
      state.msg = ''
      state.error = ''
    },
    [linkDevice.fulfilled]: (state, { payload }) => {
      state.msg = 'Устройство было успешно привязано'
      state.linkPending = false
    },
    [linkDevice.rejected]: (state, { payload }) => {
      state.error = payload
      state.linkPending = false
    },
    [createInstallationPoint.pending]: (state) => {
      state.geolocatePending = true
      state.error = ''
    },
    [createInstallationPoint.fulfilled]: (state) => {
      state.geolocatePending = false
    },
    [createInstallationPoint.rejected]: (state, { payload }) => {
      state.error = payload
      state.geolocatePending = false
    },
    [CLEAR_RESOURCES]: () => initialState,
    [CLEAR_STATUS]: (state) => {
      state.msg = ''
      state.error = ''
    },
  },
})

export const { setSearch } = installationsSlice.actions

export default installationsSlice.reducer
